/**
 * Onboarding-mail generator page.
 *
 * Lets the customer (an MSP) build three branded onboarding emails for their
 * own end-customers (announce / how-it-works / go-live). Everything is local-
 * only: state is persisted to localStorage and rendered into an iframe via
 * `srcDoc` for a true email preview. The HTML can be copied or downloaded so
 * the MSP can paste it into Outlook / Gmail / Brevo / Mailchimp.
 *
 * Mail bodies live in lib/onboardingMails.ts and follow the active portal
 * language (NL / EN) with a formal / casual tone toggle on top.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Palette, Settings, Upload, Copy, Download, Mail } from 'lucide-react';
import { useLang } from '../lib/i18n';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input, Select } from '../components/ui/input';
import { cn } from '../lib/utils';
import {
  DEFAULT_STATE,
  DEFAULT_PRICE_PLACEHOLDER,
  FONT_CHOICES,
  PALETTE,
  PRICE_UNITS,
  buildMails,
  type MailState,
  type TabId,
  type Tone,
} from '../lib/onboardingMails';

const STORAGE_KEY = 'xt_portal_mailgen_v1';
const MAX_LOGO_BYTES = 1024 * 1024;

function loadState(): MailState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch { /* fall through to defaults */ }
  return { ...DEFAULT_STATE };
}

export function Onboarding() {
  const { t, lang } = useLang();
  const tr = t.onboarding;

  const [state, setState] = useState<MailState>(loadState);
  const [tab, setTab]     = useState<TabId>('announce');
  const [toast, setToast] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist on every change. localStorage write is cheap and the state is
  // small (~few KB even with a logo data-URL).
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota / private mode */ }
  }, [state]);

  // Default priceUnit follows the active language whenever the user hasn't
  // picked one yet (or picked one from the other language's list).
  useEffect(() => {
    setState(s => PRICE_UNITS[lang].includes(s.priceUnit) ? s : { ...s, priceUnit: PRICE_UNITS[lang][0] });
  }, [lang]);

  const mails = useMemo(() => buildMails(state, lang), [state, lang]);
  const current = mails[tab];

  // ── helpers ─────────────────────────────────────────────────────────────
  const update = useCallback(<K extends keyof MailState>(key: K, value: MailState[K]) => {
    setState(s => ({ ...s, [key]: value }));
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1900);
  }, []);

  const copy = useCallback(async (text: string, msg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(msg);
    } catch {
      // Fallback for older browsers / non-secure contexts. Cleaner than a
      // user-visible "could not copy" because most callers will succeed.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); showToast(msg); }
      catch { showToast(tr.toast.copyFailed); }
      document.body.removeChild(ta);
    }
  }, [showToast, tr.toast.copyFailed]);

  const downloadCurrent = useCallback(() => {
    const blob = new Blob([current.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const co = (state.company.trim() || 'mail').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.href = url;
    a.download = `${co}-${tr.fileSlugs[tab]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast(tr.toast.downloaded);
  }, [current.html, state.company, tab, tr.fileSlugs, tr.toast.downloaded, showToast]);

  // ── logo handling ───────────────────────────────────────────────────────
  const readLogo = useCallback((file: File) => {
    if (!/^image\//.test(file.type)) { showToast(tr.toast.notAnImage); return; }
    if (file.size > MAX_LOGO_BYTES)  { showToast(tr.toast.logoTooBig); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setState(s => ({ ...s, logo: String(reader.result ?? ''), logoName: file.name }));
    };
    reader.readAsDataURL(file);
  }, [showToast, tr.toast.notAnImage, tr.toast.logoTooBig]);

  const onLogoInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (f) readLogo(f);
  };
  const onLogoDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files?.[0]; if (f) readLogo(f);
  };

  // ── reset ───────────────────────────────────────────────────────────────
  const reset = () => {
    if (!window.confirm(tr.details.resetConfirm)) return;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setState({ ...DEFAULT_STATE, priceUnit: PRICE_UNITS[lang][0] });
  };

  // ── view ────────────────────────────────────────────────────────────────
  const tabs: Array<{ id: TabId; num: string; title: string }> = [
    { id: 'announce', num: tr.preview.announceNum, title: tr.preview.announceTitle },
    { id: 'how',      num: tr.preview.howNum,      title: tr.preview.howTitle },
    { id: 'golive',   num: tr.preview.goliveNum,   title: tr.preview.goliveTitle },
  ];

  return (
    <div className="max-w-7xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{tr.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{tr.subtitle}</p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[380px_1fr] items-start">

        {/* ── SETTINGS COLUMN ──────────────────────────────────────────── */}
        <div className="space-y-5">

          {/* Company card */}
          <Card>
            <SectionHead icon={<Building2 className="h-4 w-4" />} title={tr.company.title} />
            <CardContent className="space-y-4">
              <Input label={tr.company.companyLabel} value={state.company}
                placeholder={tr.company.companyPlaceholder} autoComplete="organization"
                onChange={e => update('company', e.target.value)} />
              <Input label={tr.company.websiteLabel} type="url" value={state.website}
                placeholder={tr.company.websitePlaceholder}
                onChange={e => update('website', e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                <Input label={tr.company.emailLabel} type="email" value={state.email}
                  placeholder={tr.company.emailPlaceholder}
                  onChange={e => update('email', e.target.value)} />
                <Input label={`${tr.company.phoneLabel} ${tr.company.phoneOptional}`} type="tel" value={state.phone}
                  placeholder={tr.company.phonePlaceholder}
                  onChange={e => update('phone', e.target.value)} />
              </div>

              {/* Logo dropzone */}
              <div className="space-y-1">
                <span className="block text-sm font-medium text-gray-700">
                  {tr.company.logoLabel} <span className="text-gray-400 font-normal">{tr.company.logoHint}</span>
                </span>
                {state.logo ? (
                  <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <img src={state.logo} alt="" className="max-h-9 max-w-[110px] rounded" />
                    <div className="flex-1 min-w-0 text-xs text-gray-700">
                      <strong className="block truncate">{state.logoName || 'logo'}</strong>
                      <span className="text-gray-500">{tr.company.logoUploaded}</span>
                    </div>
                    <button type="button" onClick={() => setState(s => ({ ...s, logo: '', logoName: '' }))}
                      className="text-xs text-gray-500 hover:text-red-600 underline">
                      {tr.company.logoRemove}
                    </button>
                  </div>
                ) : (
                  <label
                    onDragEnter={e => { e.preventDefault(); setDragging(true); }}
                    onDragOver ={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={e => { e.preventDefault(); setDragging(false); }}
                    onDrop={onLogoDrop}
                    className={cn(
                      'block border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
                      dragging
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-gray-200 bg-gray-50 hover:border-brand-400 hover:bg-brand-50/50',
                    )}
                  >
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={onLogoInput} />
                    <Upload className="h-5 w-5 text-gray-400 mx-auto mb-1.5" />
                    <p className="text-xs text-gray-500">
                      <strong className="text-brand-600">{tr.company.logoDropMain}</strong> {tr.company.logoDropAux}
                    </p>
                  </label>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Brand identity card */}
          <Card>
            <SectionHead icon={<Palette className="h-4 w-4" />} title={tr.brand.title} />
            <CardContent className="space-y-4">
              {/* Colour */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">{tr.brand.primaryLabel}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={state.primary}
                    onChange={e => update('primary', e.target.value.toUpperCase())}
                    className="h-9 w-10 rounded-lg border border-gray-300 cursor-pointer p-0" />
                  <input type="text" value={state.primary} maxLength={7}
                    onChange={e => {
                      let v = e.target.value.trim();
                      if (v && v[0] !== '#') v = '#' + v;
                      update('primary', v);
                    }}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono uppercase
                               focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
                </div>
                <div role="group" aria-label={tr.brand.paletteAria} className="flex flex-wrap gap-1.5 pt-1">
                  {PALETTE.map(c => (
                    <button key={c} type="button" onClick={() => update('primary', c)}
                      aria-label={c}
                      style={{ background: c }}
                      className={cn(
                        'h-7 w-7 rounded-md cursor-pointer ring-1 ring-black/10',
                        state.primary.toUpperCase() === c.toUpperCase() && 'ring-2 ring-offset-1 ring-gray-900',
                      )} />
                  ))}
                </div>
              </div>

              <Select label={tr.brand.fontLabel} value={state.font}
                onChange={e => update('font', e.target.value)}>
                {FONT_CHOICES.map(f => (
                  <option key={f.value} value={f.value}>
                    {lang === 'nl' ? f.labelNl : f.labelEn}
                  </option>
                ))}
              </Select>

              {/* Tone segmented */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">{tr.brand.toneLabel}</label>
                <div className="flex gap-1.5">
                  {(['formal', 'casual'] as Tone[]).map(v => (
                    <button key={v} type="button" onClick={() => update('tone', v)}
                      className={cn(
                        'flex-1 px-2 py-2 rounded-lg text-xs font-semibold border transition-colors',
                        state.tone === v
                          ? 'bg-brand-600 text-white border-brand-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                      )}>
                      {v === 'formal' ? tr.brand.toneFormal : tr.brand.toneCasual}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Details card */}
          <Card>
            <SectionHead icon={<Settings className="h-4 w-4" />} title={tr.details.title} />
            <CardContent className="space-y-4">
              <Input label={`${tr.details.serviceLabel} `} value={state.service}
                placeholder={tr.details.servicePlaceholder}
                onChange={e => update('service', e.target.value)} />
              <Input label={tr.details.goliveLabel} value={state.golive}
                placeholder={tr.details.golivePlaceholder}
                onChange={e => update('golive', e.target.value)} />

              {/* Price toggle */}
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2.5">
                <label htmlFor="mg-price-toggle" className="flex-1 text-sm text-gray-700 font-medium">{tr.details.priceToggle}</label>
                <button id="mg-price-toggle" type="button" role="switch" aria-checked={state.showPrice}
                  onClick={() => update('showPrice', !state.showPrice)}
                  className={cn(
                    'w-10 h-6 rounded-full relative transition-colors',
                    state.showPrice ? 'bg-brand-600' : 'bg-gray-300',
                  )}>
                  <span className={cn(
                    'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                    state.showPrice && 'translate-x-4',
                  )} />
                </button>
              </div>

              {state.showPrice && (
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    {tr.details.priceLabel} <span className="text-gray-400 font-normal">{tr.details.priceHint}</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" min={0} step="0.01" value={state.price}
                      placeholder={DEFAULT_PRICE_PLACEHOLDER[lang]}
                      onChange={e => update('price', e.target.value)}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
                    <select value={state.priceUnit}
                      onChange={e => update('priceUnit', e.target.value)}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent">
                      {PRICE_UNITS[lang].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <Button variant="secondary" onClick={reset} className="w-full">
                {tr.details.reset}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* ── PREVIEW COLUMN ───────────────────────────────────────────── */}
        <div>
          {/* Preview tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {tabs.map(t => (
              <button key={t.id} type="button" onClick={() => setTab(t.id)}
                className={cn(
                  'flex-1 min-w-[150px] text-left bg-white border rounded-lg px-3 py-2.5 transition-shadow',
                  tab === t.id
                    ? 'border-brand-500 ring-2 ring-brand-500/20'
                    : 'border-gray-200 hover:border-brand-200',
                )}>
                <span className="block text-[10.5px] font-mono uppercase tracking-wider text-gray-500">{t.num}</span>
                <span className="block text-sm font-semibold text-gray-900 mt-0.5">{t.title}</span>
              </button>
            ))}
          </div>

          {/* Subject + actions bar */}
          <div className="flex flex-wrap items-center gap-2 bg-white border border-b-0 border-gray-200 rounded-t-xl px-4 py-3">
            <span className="flex-1 min-w-[120px] text-xs text-gray-500">
              {tr.preview.subjectLabel} <strong className="text-gray-800 font-semibold">{current.subject || '—'}</strong>
            </span>
            <Button size="sm" variant="secondary" onClick={() => copy(current.subject, tr.toast.subjectCopied)}>
              <Copy className="h-3.5 w-3.5" /> {tr.preview.copySubject}
            </Button>
            <Button size="sm" onClick={() => copy(current.html, tr.toast.htmlCopied)}>
              <Copy className="h-3.5 w-3.5" /> {tr.preview.copyHtml}
            </Button>
            <Button size="sm" variant="secondary" onClick={downloadCurrent}>
              <Download className="h-3.5 w-3.5" /> {tr.preview.download}
            </Button>
          </div>

          {/* iframe */}
          <div className="border border-gray-200 rounded-b-xl bg-[#EDF0F5] p-5">
            <iframe
              title={tr.preview.iframeTitle}
              srcDoc={current.html}
              className="w-full h-[720px] border-0 rounded-lg bg-white shadow-lg"
            />
          </div>

          <p className="mt-3 text-sm text-gray-500">{tr.preview.help}</p>
        </div>

      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm font-medium
                        px-5 py-3 rounded-full shadow-xl z-50 flex items-center gap-2">
          <Mail className="h-4 w-4 text-green-400" />
          {toast}
        </div>
      )}
    </div>
  );
}

// Internal: card section header — mirrors the existing pages' look without
// reaching for a new shared component (used only here, three times).
function SectionHead({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100">
      <span className="text-brand-600">{icon}</span>
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
    </div>
  );
}
